import {
    Box,
    Button,
    ButtonBase,
    Divider,
    IconButton,
    ListSubheader,
    Menu,
    Tooltip,
} from '@mui/material';
import { CircleDashed, Scan, UserRound, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CheckboxMenuItem } from '~/components/Menu';
import {
    activeFilterChips,
    filterOptionLabel,
    hasActivePatientFilters,
    isFilterSelected,
    PATIENT_FILTER_MENU_FACETS,
    type PatientFilterFacetId,
    type PatientFilters,
} from './model';

/** Tall enough for every dimension at once on a normal window; scrolls below that. */
const MENU_MAX_HEIGHT = 560;

/** Leading glyph of a chip, so a dimension is recognisable before its label is read. */
const FACET_ICONS: Record<PatientFilterFacetId, LucideIcon> = {
    courseStage: CircleDashed,
    status: UserRound,
    modality: Scan,
};

/**
 * The open filter menu: which control opened it (for that control's expanded
 * state), and one facet's options when opened from that facet's chip.
 */
export type FilterMenuState =
    | { origin: 'toolbar'; anchor: HTMLElement }
    | { origin: 'chip'; anchor: HTMLElement; facetId: PatientFilterFacetId };

export type PatientListFilterBarProps = {
    filters: PatientFilters;
    onToggle: (facetId: PatientFilterFacetId, value: string) => void;
    onClearFacet: (facetId: PatientFilterFacetId) => void;
    onClear: () => void;
    /** Owned by the screen, since the toolbar's control opens it too. */
    menu: FilterMenuState | undefined;
    onMenuChange: (menu: FilterMenuState | undefined) => void;
    menuId: string;
};

/**
 * Active-filter group and the filter menu for the menu dimensions (patient
 * status, modality). The group shows whenever any dimension is active, the
 * strip's and the assignee toggle's included, so the footer's matching-count
 * is never presented with no filter in sight and clearing everything at once
 * is always one control away; it carries a chip per menu dimension only,
 * since the strip and the toggle show their own selection. It takes the rest
 * of the filter row it is placed in, dropping below it when it does not fit.
 * The menu is always mounted here even filterless, anchored to whichever
 * control opened it.
 */
export default function PatientListFilterBar({
    filters,
    onToggle,
    onClearFacet,
    onClear,
    menu,
    onMenuChange,
    menuId,
}: PatientListFilterBarProps) {
    const intl = useIntl();
    const chips = activeFilterChips(intl, filters);

    const closeMenu = () => onMenuChange(undefined);

    // Latched so the closing transition still shows the facet it had, not a
    // jump to every facet before the menu fades out.
    const lastMenuRef = useRef<FilterMenuState>();
    if (menu !== undefined) {
        lastMenuRef.current = menu;
    }
    const shownMenu = menu ?? lastMenuRef.current;

    const shownFacets =
        shownMenu?.origin === 'chip'
            ? PATIENT_FILTER_MENU_FACETS.filter(
                  (facet) => facet.id === shownMenu.facetId
              )
            : PATIENT_FILTER_MENU_FACETS;

    return (
        <>
            {hasActivePatientFilters(filters) && (
                <Box
                    role="group"
                    aria-label={intl.formatMessage({
                        id: 'patientList.filter.activeRegion',
                    })}
                    sx={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.75,
                    }}
                >
                    {chips.map((chip) => {
                        const Icon = FACET_ICONS[chip.facetId];
                        const chipMenuOpen =
                            menu?.origin === 'chip' &&
                            menu.facetId === chip.facetId;
                        return (
                            <Box
                                key={chip.facetId}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'stretch',
                                    height: 28,
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: 'divider',
                                    bgcolor: 'background.raised',
                                    overflow: 'hidden',
                                    fontSize: 13,
                                }}
                            >
                                <Box sx={SEGMENT_SX}>
                                    <Icon size={13} aria-hidden />
                                    {chip.facetLabel}
                                </Box>
                                <ChipDivider />
                                <Box
                                    sx={{
                                        ...SEGMENT_SX,
                                        color: 'text.secondary',
                                    }}
                                >
                                    <FormattedMessage id="patientList.filter.operator.isAnyOf" />
                                </Box>
                                <ChipDivider />
                                <Tooltip
                                    title={chip.valueLabels.join(', ')}
                                    placement="bottom-start"
                                >
                                    <ButtonBase
                                        onClick={(event) =>
                                            onMenuChange({
                                                origin: 'chip',
                                                anchor: event.currentTarget,
                                                facetId: chip.facetId,
                                            })
                                        }
                                        aria-haspopup="true"
                                        aria-expanded={chipMenuOpen}
                                        aria-controls={
                                            chipMenuOpen ? menuId : undefined
                                        }
                                        aria-label={intl.formatMessage(
                                            {
                                                id: 'patientList.filter.change',
                                            },
                                            { facet: chip.facetLabel }
                                        )}
                                        sx={{
                                            ...SEGMENT_SX,
                                            font: 'inherit',
                                            fontWeight: 500,
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                            },
                                        }}
                                    >
                                        {chip.valueSummary}
                                    </ButtonBase>
                                </Tooltip>
                                <ChipDivider />
                                <IconButton
                                    size="small"
                                    disableRipple
                                    onClick={() => onClearFacet(chip.facetId)}
                                    aria-label={intl.formatMessage(
                                        { id: 'patientList.filter.remove' },
                                        { facet: chip.facetLabel }
                                    )}
                                    sx={{
                                        px: 0.5,
                                        borderRadius: 0,
                                        color: 'text.secondary',
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                            color: 'text.primary',
                                        },
                                    }}
                                >
                                    <X size={13} />
                                </IconButton>
                            </Box>
                        );
                    })}

                    <Button size="small" onClick={onClear}>
                        <FormattedMessage id="patientList.filter.clear" />
                    </Button>
                </Box>
            )}

            <Menu
                id={menuId}
                anchorEl={menu?.anchor}
                open={menu !== undefined}
                onClose={closeMenu}
                // 'menu' not the default 'selectedMenu': that scrolls the checked option
                // into view, hiding dimensions above it when a filter is already active.
                variant="menu"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { maxHeight: MENU_MAX_HEIGHT } } }}
            >
                {shownFacets.flatMap((facet, facetIndex) => [
                    ...(facetIndex > 0
                        ? [<Divider key={`${facet.id}-divider`} />]
                        : []),
                    <ListSubheader key={`${facet.id}-header`}>
                        <FormattedMessage id={facet.labelId} />
                    </ListSubheader>,
                    ...facet.options.map((option) => (
                        <CheckboxMenuItem
                            key={`${facet.id}-${option.value}`}
                            checked={isFilterSelected(
                                filters,
                                facet.id,
                                option.value
                            )}
                            label={filterOptionLabel(intl, option)}
                            // Stays open since multi-select per dimension is common.
                            onClick={() => onToggle(facet.id, option.value)}
                        />
                    )),
                ])}
            </Menu>
        </>
    );
}

/** One labelled cell of a chip. */
const SEGMENT_SX = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 0.75,
    whiteSpace: 'nowrap',
} as const;

function ChipDivider() {
    return <Divider orientation="vertical" flexItem />;
}
